/**
 * IXVerify v2 — slim in-wizard header (ixv2 WP-3.2; plan §4).
 *
 * The dispense wizard is the one allowed full-screen surface (R7) — no
 * AppBar; instead: exit ✕, step dots, site name, and (while the dispense is
 * in flight) a cancel control. Safe-area aware (viewport-fit=cover phones).
 *
 * Owner feedback 2026-10-03 ("I don't like all the pop ups that stop you"):
 * ✕ LEAVES — the session is resumable from the dashboard, so no "Leave this
 * dispense?" dialog stands in the way. Cancelling for good is the trash
 * control, armed on the first tap ("Tap again to cancel", auto-disarms) and
 * done on the second — a two-tap confirm in place, not a modal.
 */
import { useEffect, useState } from 'react';
import { Trash2, X } from 'lucide-react';
import type { WizardStep } from '../../lib/captureMachine';

const STEP_DOTS: Array<{ key: string; matches: WizardStep[] }> = [
  { key: 'capture', matches: ['start', 'capture'] },
  { key: 'review', matches: ['review'] },
  { key: 'attest', matches: ['attest'] },
  { key: 'done', matches: ['published', 'mismatch', 'closed'] },
];

const CANCEL_ARM_MS = 4000;

export default function WizardHeader({
  step,
  siteName,
  onExit,
  onCancel,
  cancelling = false,
}: {
  step: WizardStep;
  siteName: string | null;
  onExit: () => void;
  /** Cancel the dispense for good — offered only while it is in flight. */
  onCancel?: () => void;
  cancelling?: boolean;
}) {
  const activeIdx = STEP_DOTS.findIndex((d) => d.matches.includes(step));
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    if (!armed) return undefined;
    const t = window.setTimeout(() => setArmed(false), CANCEL_ARM_MS);
    return () => window.clearTimeout(t);
  }, [armed]);
  return (
    <header
      className="flex h-12 flex-shrink-0 items-center justify-between gap-2 border-b border-gray-800 bg-gray-950 px-3 text-white"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <button
        type="button"
        aria-label="Exit dispense"
        onClick={onExit}
        className="inline-flex h-11 w-11 items-center justify-center rounded-lg text-white/80 transition-colors duration-150 hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 motion-reduce:transition-none sm:h-9 sm:w-9"
      >
        <X className="h-5 w-5" aria-hidden="true" />
      </button>

      <div className="flex items-center gap-1.5" aria-label="Dispense progress" role="img">
        {STEP_DOTS.map((d, i) => (
          <span
            key={d.key}
            className={`h-1.5 rounded-full transition-all duration-150 motion-reduce:transition-none ${
              i === activeIdx ? 'w-6 bg-primary-500' : i < activeIdx ? 'w-1.5 bg-primary-500/60' : 'w-1.5 bg-white/25'
            }`}
          />
        ))}
      </div>

      <div className="flex min-w-0 items-center justify-end gap-1">
        {!armed && <div className="max-w-[40vw] truncate text-right text-xs text-white/70">{siteName || ''}</div>}
        {onCancel && (
          <button
            type="button"
            aria-label={armed ? 'Tap again to cancel this dispense' : 'Cancel dispense'}
            disabled={cancelling}
            onClick={() => {
              if (armed) {
                setArmed(false);
                onCancel();
              } else {
                setArmed(true);
              }
            }}
            data-testid={armed ? 'cancel-dispense-armed' : 'cancel-dispense'}
            className={`inline-flex h-11 items-center justify-center gap-1.5 rounded-lg px-2 text-xs font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none sm:h-9 ${
              armed ? 'bg-red-600 text-white hover:bg-red-700' : 'w-11 text-white/80 hover:bg-white/10 hover:text-white sm:w-9'
            }`}
          >
            <Trash2 className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
            {armed && <span className="whitespace-nowrap">Tap again to cancel</span>}
          </button>
        )}
      </div>
    </header>
  );
}
